import { useQuery } from '@tanstack/react-query';
import { Select, Spin, Switch } from 'antd';
import { Field, FieldProps, Form, Formik, useFormikContext } from 'formik';

import { Button } from '../../../custom';
import {
  SchoolFeatureConfig,
  useGetSchoolConfigQuery,
} from '../../../hooks/api/schoolConfig/useGetSchoolConfigQuery';
import { useUpdateSchoolConfigMutation } from '../../../hooks/api/schoolConfig/useUpdateSchoolConfigMutation';
import { getEmailTemplates } from '../../userManagement/emailTemplates/request';
import { getRoles } from '../../userManagement/request';

import styles from './schoolConfig.module.scss';

interface SwitchFieldProps {
  label: string;
  name: keyof SchoolFeatureConfig;
}

interface SelectFieldProps {
  label: string;
  name: keyof SchoolFeatureConfig;
  loading?: boolean;
  mode?: 'multiple';
  options: { label: string; value: string }[];
  placeholder: string;
}

interface NumberFieldProps {
  label: string;
  name: keyof SchoolFeatureConfig;
  placeholder: string;
}

interface EnumSelectFieldProps {
  label: string;
  name: keyof SchoolFeatureConfig;
  options: { label: string; value: number }[];
  placeholder: string;
}

const targetStatusOptions = [
  { label: 'Under Review', value: 'UnderReview' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Approved', value: 'Approved' },
  { label: 'Rejected', value: 'Rejected' },
];

// Mirrors the backend UsernameFormat enum.
const usernameFormatOptions = [
  { label: 'Application Number', value: 1 },
  { label: 'Email', value: 2 },
  { label: 'Matric Number', value: 3 },
];

// Mirrors the backend DefaultPasswordFormat enum.
const defaultPasswordFormatOptions = [
  { label: 'Surname (lowercase)', value: 1 },
  { label: 'Surname (UPPERCASE)', value: 2 },
  { label: 'Application Number', value: 3 },
  { label: 'Date of Birth', value: 4 },
  { label: 'Random Alphanumeric', value: 5 },
  { label: 'Matric Number (UPPERCASE)', value: 6 },
  { label: 'Matric Number (lowercase)', value: 7 },
];

// Mirrors the backend CourseChangeLockStage enum.
const courseChangeLockStageOptions = [
  { label: 'After Submission', value: 1 },
  { label: 'After Application Fee Payment', value: 2 },
  { label: 'Never', value: 3 },
  { label: 'Always', value: 4 },
];

const defaultSchoolConfig: SchoolFeatureConfig = {
  id: 0,
  abandonedNotificationsEnabled: false,
  abandonedNotificationIntervalDays: 0,
  abandonedNotificationMaxReminders: 0,
  abandonedNotificationEmailTemplateCode: '',
  allowCourseChange: false,
  courseChangeLockStage: 0,
  concentrationSelectionEnabled: false,
  allowPostRejectionOverride: false,
  postRejectionOverrideRoles: '',
  postRejectionTargetStatus: '',
  enableMultilevelAdmissionApproval: false,
  autoProvisionCredentials: false,
  usernameFormat: 0,
  defaultPasswordFormat: 0,
  generateMatricBeforeMove: false,
};

const splitRoles = (roles?: string) => {
  if (!roles) {
    return [];
  }

  return roles
    .split(',')
    .map((role) => role.trim())
    .filter(Boolean);
};

const SwitchField = ({ label, name }: SwitchFieldProps) => {
  const { setFieldValue, values } = useFormikContext<SchoolFeatureConfig>();

  return (
    <div className={styles.switchField}>
      <label>{label}</label>
      <div className={styles.control}>
        <Switch checked={Boolean(values[name])} onChange={(checked) => setFieldValue(name, checked)} />
      </div>
    </div>
  );
};

const NumberField = ({ label, name, placeholder }: NumberFieldProps) => {
  return (
    <Field name={name}>
      {({ field, meta }: FieldProps) => (
        <div className={styles.field}>
          <label>{label}</label>
          <div className={styles.control}>
            <input {...field} className={styles.numberInput} min={0} placeholder={placeholder} type="number" />
            {meta.touched && meta.error && <div className={styles.error}>{meta.error}</div>}
          </div>
        </div>
      )}
    </Field>
  );
};

const SelectField = ({ label, name, options, placeholder, loading, mode }: SelectFieldProps) => {
  const { setFieldValue, values } = useFormikContext<SchoolFeatureConfig>();
  const value = values[name] as string;

  return (
    <div className={styles.selectField}>
      <label>{label}</label>
      <div className={styles.control}>
        <Select
          allowClear
          loading={loading}
          mode={mode}
          options={options}
          placeholder={placeholder}
          size="large"
          style={{ width: '100%' }}
          value={mode === 'multiple' ? splitRoles(value) : value || undefined}
          onChange={(nextValue) => {
            setFieldValue(name, Array.isArray(nextValue) ? nextValue.join(',') : nextValue ?? '');
          }}
        />
      </div>
    </div>
  );
};

const EnumSelectField = ({ label, name, options, placeholder }: EnumSelectFieldProps) => {
  const { setFieldValue, values } = useFormikContext<SchoolFeatureConfig>();
  const value = values[name] as number;

  return (
    <div className={styles.selectField}>
      <label>{label}</label>
      <div className={styles.control}>
        <Select
          allowClear
          options={options}
          placeholder={placeholder}
          size="large"
          style={{ width: '100%' }}
          value={value || undefined}
          onChange={(nextValue) => setFieldValue(name, nextValue ?? 0)}
        />
      </div>
    </div>
  );
};

const SchoolConfig = () => {
  const { schoolConfigData, isLoading, isError, errorMessage } = useGetSchoolConfigQuery();
  const { updateSchoolConfigHandler, isUpdateLoading } = useUpdateSchoolConfigMutation();

  const { data: roles, isLoading: isRolesLoading } = useQuery({
    queryKey: ['get-roles'],
    queryFn: getRoles,
  });

  const { data: emailTemplates, isLoading: isEmailTemplatesLoading } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: getEmailTemplates,
  });

  const roleOptions = roles?.map((role) => ({ label: role.name, value: role.name })) ?? [];
  const templateOptions =
    emailTemplates?.data?.map((template) => ({
      label: template.code,
      value: template.code,
    })) ?? [];

  const initialValues: SchoolFeatureConfig = {
    ...defaultSchoolConfig,
    ...schoolConfigData,
    abandonedNotificationEmailTemplateCode:
      schoolConfigData?.abandonedNotificationEmailTemplateCode === 'null'
        ? ''
        : schoolConfigData?.abandonedNotificationEmailTemplateCode ?? '',
  };

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (isError) {
    return <div>Error: {errorMessage}</div>;
  }

  return (
    <main>
      <section className="space-between">
        <h3>School Feature Config</h3>
      </section>

      <section className={styles.formCard}>
        <Formik
          initialValues={initialValues}
          onSubmit={(values, { resetForm }) => updateSchoolConfigHandler(values, resetForm)}
          enableReinitialize
        >
          <Form>
            <div className={styles.section}>
              <h4>Application Recovery</h4>
              <div className={styles.grid}>
                <SwitchField label="Abandoned Notifications" name="abandonedNotificationsEnabled" />
                <NumberField
                  label="Notification Interval Days"
                  name="abandonedNotificationIntervalDays"
                  placeholder="Enter interval days"
                />
                <NumberField
                  label="Max Reminders"
                  name="abandonedNotificationMaxReminders"
                  placeholder="Enter max reminders"
                />
                <SelectField
                  label="Email Template"
                  name="abandonedNotificationEmailTemplateCode"
                  options={templateOptions}
                  placeholder="Select email template"
                  loading={isEmailTemplatesLoading}
                />
              </div>
            </div>

            <div className={styles.section}>
              <h4>Course And Concentration</h4>
              <div className={styles.grid}>
                <SwitchField label="Allow Course Change" name="allowCourseChange" />
                <EnumSelectField
                  label="Course Change Lock Stage"
                  name="courseChangeLockStage"
                  options={courseChangeLockStageOptions}
                  placeholder="Select lock stage"
                />
                <SwitchField label="Concentration Selection" name="concentrationSelectionEnabled" />
              </div>
            </div>

            <div className={styles.section}>
              <h4>Post Rejection Override</h4>
              <div className={styles.grid}>
                <SwitchField label="Allow Override" name="allowPostRejectionOverride" />
                <SelectField
                  label="Override Roles"
                  name="postRejectionOverrideRoles"
                  options={roleOptions}
                  placeholder="Select roles"
                  loading={isRolesLoading}
                  mode="multiple"
                />
                <SelectField
                  label="Target Status"
                  name="postRejectionTargetStatus"
                  options={targetStatusOptions}
                  placeholder="Select target status"
                />
              </div>
            </div>

            <div className={styles.section}>
              <h4>Admission Approval</h4>
              <div className={styles.grid}>
                <SwitchField label="Enable Multilevel Admission Approval" name="enableMultilevelAdmissionApproval" />
              </div>
            </div>

            <div className={styles.section}>
              <h4>Credential Provisioning</h4>
              <div className={styles.grid}>
                <SwitchField label="Auto Provision Credentials" name="autoProvisionCredentials" />
                <EnumSelectField
                  label="Username Format"
                  name="usernameFormat"
                  options={usernameFormatOptions}
                  placeholder="Select username format"
                />
                <EnumSelectField
                  label="Default Password Format"
                  name="defaultPasswordFormat"
                  options={defaultPasswordFormatOptions}
                  placeholder="Select password format"
                />
                <SwitchField label="Generate Matric Before Move" name="generateMatricBeforeMove" />
              </div>
            </div>

            <div className={styles.actions}>
              <Button
                type="submit"
                disabled={isUpdateLoading}
                isLoading={isUpdateLoading}
                text={isUpdateLoading ? 'Saving...' : 'Save Changes'}
              />
            </div>
          </Form>
        </Formik>
      </section>
    </main>
  );
};

export default SchoolConfig;
